import { fetchApiBlob, http } from './client';
import type {
  AnswersResponse,
  AuthResponse,
  ChatHistoryResponse,
  ConfirmCodePayload,
  CreateProfilePayload,
  DoctorProfile,
  LessonResponse,
  LessonsResponse,
  Platform,
  ModulesResponse,
  ProfilesResponse,
  QuestionsResponse,
  RequestCodePayload,
  SaveAnswerPayload,
  StartChatPayload,
  StartChatResponse,
  StartChatSession,
  SubmissionRun,
  SmmToolsResponse,
} from './types';

/**
 * Типизированный клиент бэкенда мини-приложения по FRONTEND_API.md.
 *
 * Один метод — одна ручка контракта, без бизнес-логики. Логика состояний
 * (когда что вызывать, как реагировать на ошибки) живёт в сторах.
 */
export const miniappApi = {
  // 1. Вход и сессия
  auth: (platform: Platform, initData: string) =>
    http.post<AuthResponse>('/api/miniapp/auth', { platform, initData }),

  session: () => http.post<AuthResponse>('/api/miniapp/session', {}),

  // 2. Подтверждение email (только при state = registration)
  requestEmailCode: (email: string) =>
    http.post<AuthResponse>('/api/miniapp/registration', {
      action: 'request_code',
      email,
    } satisfies RequestCodePayload),

  confirmEmailCode: (email: string, code: string) =>
    http.post<AuthResponse>('/api/miniapp/registration', {
      action: 'confirm_code',
      email,
      code,
    } satisfies ConfirmCodePayload),

  // 3. Согласие, профили и анкета
  /** Возвращает только `{ consent: true }`, а не состояние сессии. */
  agreeConsent: () => http.post<{ consent: boolean }>('/api/miniapp/agree-consent', { accepted: true }),

  getProfiles: () => http.get<ProfilesResponse>('/api/miniapp/profiles'),

  createProfile: (payload: CreateProfilePayload) =>
    http.post<DoctorProfile>('/api/miniapp/create-profile', payload),

  selectProfile: (doctorProfileId: number) =>
    http.post<DoctorProfile>('/api/miniapp/select-profile', { doctor_profile_id: doctorProfileId }),

  // Ответы приходят завёрнутыми: анкета в `questions`, ответы в `answers`.
  // Разворачивается здесь, чтобы обёртки бэкенда не расползались по сторам
  getQuestions: async () =>
    (await http.get<QuestionsResponse>('/api/miniapp/questions')).questions ?? [],

  getAnswers: async () => (await http.get<AnswersResponse>('/api/miniapp/answers')).answers ?? {},

  saveAnswer: (payload: SaveAnswerPayload) => http.post<void>('/api/miniapp/answer', payload),

  completeSurvey: () => http.post<AuthResponse>('/api/miniapp/complete', {}),

  // 4. Курсы и уроки
  /** Модули курса; в ответе они лежат в `courses`. */
  getModules: async () => (await http.get<ModulesResponse>('/api/miniapp/modules')).courses ?? [],

  /** Уроки модуля вместе с прогрессом по нему. */
  getLessons: (courseId: string) =>
    http.get<LessonsResponse>('/api/miniapp/lessons', { course_id: courseId }),

  /** Подробности урока: инструкция, подсказка, материалы и файлы. */
  getLesson: async (assignmentId: number | string) =>
    (await http.get<LessonResponse>('/api/miniapp/lesson', { assignment_id: assignmentId })).lesson,

  // 5. AI-чат
  /**
   * Создаёт чат либо возвращает уже активный.
   *
   * Форма ответа различается: новый чат приходит завёрнутым в `data`, а
   * продолжение активного — плоским объектом с `session_id` и `resumed`
   * наверху. Разбирать это в сторе нельзя: любая из форм должна давать
   * одинаковый результат.
   */
  startChat: async (payload: StartChatPayload) => {
    const response = await http.post<StartChatResponse>('/api/miniapp/start-chat-ai', payload);
    if (response.data?.session_id) return response.data;
    return {
      session_id: response.session_id ?? '',
      resumed: response.resumed === true,
    } satisfies StartChatSession;
  },

  sendChatMessage: (text: string) => http.post<unknown>('/api/miniapp/send-message-chat-ai', { text }),

  getChatHistory: () => http.get<ChatHistoryResponse>('/api/miniapp/history-chat'),

  /** Ответ агента на файл приходит асинхронно: историю нужно перечитать. */
  uploadAiFile: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return http.postForm<unknown>('/api/miniapp/upload-ai-file', form);
  },

  /** Голосовой ответ. Сервер принимает только WAV, других форматов не берёт. */
  uploadAiAudio: (audio: Blob) => {
    const form = new FormData();
    form.append('file', audio, 'voice.wav');
    return http.postForm<unknown>('/api/miniapp/upload-ai-audio', form);
  },

  /** Инструменты ассистента: отдельный список, вне программы курса. */
  getSmmTools: async () => {
    const response = await http.get<SmmToolsResponse>('/api/miniapp/smm-tools');
    return response.tools ?? [];
  },

  /** Содержимое вложения через прокси бэкенда; прямой адрес AI-сервиса скрыт. */
  downloadAiFile: (url: string) => fetchApiBlob(url),

  /**
   * Файл урока по готовому адресу от бэкенда.
   *
   * Скачивается запросом, а не переходом по ссылке: переход в WebView заменяет
   * страницу и выгружает приложение вместе с открытым уроком.
   */
  downloadLessonFile: (url: string) => fetchApiBlob(url),

  /** Ставит задачу в очередь: результат придёт в диалог сообщением с тем же run_id. */
  submissionPreview: () => http.post<SubmissionRun>('/api/miniapp/submission-preview', {}),

  finalAnswer: () => http.post<SubmissionRun>('/api/miniapp/final-answer', {}),

  cancelChat: () => http.post<unknown>('/api/miniapp/cancel-chat-ai', {}),
};

export type MiniappApi = typeof miniappApi;
